'use client';
import { useFormatter, useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useStore } from '@/app/providers/store';
import type { ClawPassProduct } from '@/app/types';
import { useLocale } from '@/i18n/routing';
import { computeClawAdvanceWindow, getEffectiveValidTo } from './utils';

// CJK 语言 plan 名与后缀直接相连（"尊享版权益包"），其余语言用空格分隔（"Pro Pass"）
const CJK_LOCALES = new Set(['zh', 'ja', 'ko']);

interface ClawAdvancePurchaseInfoProps {
  /** 本次购买的商品 */
  product: ClawPassProduct;
  /** 本次购买时长（月） */
  quantity: number;
}

/**
 * Claw 权益包购买前的额外说明信息。
 * 国内（支付宝）已在 Alipay BottomTips 中呈现等价信息；本组件用于海外 Stripe
 * 直接跳转支付前的确认弹窗，补齐缺失的提示。
 *
 * 两种触发场景（可叠加）：
 * 1. 叠加续购（has_benefit 且非 trial）：当前套餐有效期、到期后生效、预计生效~结束时间
 * 2. Cross-buy 到 pass（已有 bundle 用户买 pass）：提示新购不含会员权益、bundle 到期后影响
 */
export const ClawAdvancePurchaseInfo = ({ product, quantity }: ClawAdvancePurchaseInfoProps) => {
  const t = useTranslations();
  const format = useFormatter();
  const locale = useLocale();
  const clawPass = useStore((state) => state.clawPass);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const advanceWindow = useMemo(
    // 走最终到期（叠加续购后是 active_benefit_periods 数组第一项）
    () => computeClawAdvanceWindow(getEffectiveValidTo(clawPass), quantity),
    [clawPass, quantity],
  );
  // Bundle 用户买 Pass：本次购买不含会员权益，bundle 到期后影响 10s+ 视频
  const isCrossToPass = clawPass?.product_type === 'claw_pass_bundle' && product.product_type === 'claw_pass';

  const formatDate = (date: Date) =>
    format.dateTime(date, { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric' });

  // 展示「当前持有套餐」名：跟随用户当前权益的档位与类型（而非本次购买的商品）
  const currentPlanType = clawPass?.claw_pass_plan_type === 'claw_pass_pro' ? 'claw_pass_pro' : 'claw_pass_basic';
  const currentSuffixKey =
    clawPass?.product_type === 'claw_pass_bundle' ? 'claw_pass_bundle_suffix' : 'claw_pass_pass_suffix';
  const joiner = CJK_LOCALES.has(locale) ? '' : ' ';
  const planName = `${t(`payments.${currentPlanType}`)}${joiner}${t(`payments.${currentSuffixKey}`)}`;

  const lines: string[] = [];
  if (advanceWindow) {
    lines.push(
      t('payments.stripeConfirm.clawCurrentValidUntil', {
        plan: planName,
        date: formatDate(advanceWindow.currentExpire),
      }),
      t('payments.plans.clawAdvancePurchaseHint'),
      t('payments.stripeConfirm.clawOrderRange', {
        start: formatDate(advanceWindow.effectiveStart),
        end: formatDate(advanceWindow.effectiveEnd),
      }),
    );
  }
  if (isCrossToPass) {
    lines.push(t('payments.stripeConfirm.clawCrossToPassWarning'));
  }

  if (lines.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 rounded-12 bg-system-white08 p-3 text-sm text-system-white64 leading-[20px]">
      {lines.map((line) => (
        <div key={line} className="flex gap-1.5">
          <span className="shrink-0 text-system-white36">•</span>
          <span>{line}</span>
        </div>
      ))}
    </div>
  );
};
